// localStorage-backed persistence + user-state transitions on lesson finish.

import {
  CURRICULUM,
  getCheckpoint,
  getTrin,
  MINI_CHECKPOINTS,
} from '../data/curriculum';
import { PROGRESSION } from './featureFlags';
import {
  ALL_TRINS,
  allTrinIdsInTier,
  firstTrinIdInTier,
  getNextTrin,
  trinIndex,
} from './curriculum';
import type {
  AppState,
  AppStateVersion,
  Badge,
  BadgeId,
  BaselineSnapshot,
  CheckpointRecord,
  CurriculumTierId,
  DailySummary,
  LessonResult,
  MiniCheckpointResult,
  TrinProofResult,
  TrinRecord,
  UserGoals,
  UserPreferences,
  UserPreferencesPatch,
  UserProfile,
  UserRole,
  WpmCheckIn,
} from '../types';
import {
  migrateBaselineTranscription,
  normalizeBaselineSnapshot,
  saveBaseline as mergeBaseline,
} from './baseline';
import { normalizeUserGoals } from './goals';
import {
  backfillDailySummariesFromRecentResults,
  localDateFromIso,
  mergeCheckpointRecord,
  mergeLessonIntoDailySummary,
  mergeLessonIntoTrinRecord,
  pruneDailySummaries,
  xpForLesson,
} from './stats';
import { FIRST_TRIN_ID } from './curriculum';
import { migrateUserToV31 } from './migrateV31';
import { migrateUserToV32 } from './migrateV32';
import { migrateUserToV33 } from './migrateV33';
import { migrateUserToV34 } from './migrateV34';
import { migrateUserToV35 } from './migrateV35';
import {
  activityPayloadFromBaseline,
  activityPayloadFromLesson,
  estimateKeystrokesFromWords,
  recordActivity,
} from './activityLog';
import { migrateTrinId, migrateTrinIdList } from './trinMigration';
import { clampMentalImageryRepeats } from './mentalImagery';
import { clampMetronomeBpm } from './presentationTempo';
import { WARMUP_TRIN_ID } from './warmup';
import {
  backfillTypingProfile,
  mergeTypingProfileFromLesson,
} from './typingProfile';
import {
  notebookEntryFromLegacyDraft,
  normalizeStory3Notebook,
  upsertNotebookEntry,
} from './story3Notebook';
import { isStory3ExpertId } from '../data/story3/experts';
import type { Story3ExpertId, Story3FeedbackModeId, Story3NotebookEntry } from '../types';

export const STORAGE_KEY = 'touchtype:v1';

const RECENT_RESULTS_CAP = 30;
export function defaultPreferences(): UserPreferences {
  return {
    audioEnabled: true,
    dictationVoiceId: null,
    dictationRate: 0.85,
    mentalRehearsalEnabled: true,
    metronomeEnabled: true,
    metronomeMode: 'classic',
    dictationUnitLength: 1,
    niveau4Lexicon: 'da',
    dictationLexicon: 'da',
    flowLexicon: 'da',
    mentalImageryRepeats: 1,
  };
}

function mergePreferences(
  existing: UserPreferences | undefined,
): UserPreferences {
  const d = defaultPreferences();
  if (!existing) return d;
  return {
    audioEnabled:
      typeof existing.audioEnabled === 'boolean'
        ? existing.audioEnabled
        : d.audioEnabled,
    dictationVoiceId:
      typeof existing.dictationVoiceId === 'string' ||
      existing.dictationVoiceId === null
        ? existing.dictationVoiceId
        : d.dictationVoiceId,
    dictationRate:
      typeof existing.dictationRate === 'number' &&
      Number.isFinite(existing.dictationRate)
        ? Math.max(0.5, Math.min(1.4, existing.dictationRate))
        : d.dictationRate,
    mentalRehearsalEnabled:
      typeof existing.mentalRehearsalEnabled === 'boolean'
        ? existing.mentalRehearsalEnabled
        : d.mentalRehearsalEnabled,
    metronomeEnabled:
      typeof existing.metronomeEnabled === 'boolean'
        ? existing.metronomeEnabled
        : d.metronomeEnabled,
    dictationUnitLength: clampDictationUnitLength(
      existing.dictationUnitLength ?? d.dictationUnitLength ?? 1,
    ),
    metronomeBpm:
      typeof existing.metronomeBpm === 'number' &&
      Number.isFinite(existing.metronomeBpm)
        ? clampMetronomeBpm(existing.metronomeBpm)
        : undefined,
    metronomeMode:
      existing.metronomeMode === 'survival' ? 'survival' : d.metronomeMode,
    niveau4Lexicon:
      existing.niveau4Lexicon === 'en' ? 'en' : d.niveau4Lexicon,
    dictationLexicon:
      existing.dictationLexicon === 'en' ? 'en' : d.dictationLexicon,
    flowLexicon: existing.flowLexicon === 'en' ? 'en' : d.flowLexicon,
    lastStoryId:
      typeof existing.lastStoryId === 'string'
        ? existing.lastStoryId
        : undefined,
    lastStory2SeedId:
      typeof existing.lastStory2SeedId === 'string'
        ? existing.lastStory2SeedId
        : undefined,
    lastSkrivelegGame:
      existing.lastSkrivelegGame === 'guess' ||
      existing.lastSkrivelegGame === 'mystery' ||
      existing.lastSkrivelegGame === 'persuade'
        ? existing.lastSkrivelegGame
        : undefined,
    lastMysteryVariant:
      existing.lastMysteryVariant === 'person' ||
      existing.lastMysteryVariant === 'place' ||
      existing.lastMysteryVariant === 'object'
        ? existing.lastMysteryVariant
        : undefined,
    lastGuessCategoryId:
      existing.lastGuessCategoryId === 'ting' ||
      existing.lastGuessCategoryId === 'person' ||
      existing.lastGuessCategoryId === 'sted' ||
      existing.lastGuessCategoryId === 'dyr'
        ? existing.lastGuessCategoryId
        : undefined,
    customPracticeKeys: sanitizeStoredCustomPracticeKeys(
      existing.customPracticeKeys,
    ),
    mentalImageryRepeats: clampMentalImageryRepeats(
      existing.mentalImageryRepeats ?? d.mentalImageryRepeats ?? 1,
    ),
    lastStory3FeedbackMode: sanitizeStory3FeedbackMode(
      existing.lastStory3FeedbackMode,
    ),
    lastStory3ExpertId: sanitizeStory3ExpertId(existing.lastStory3ExpertId),
    lastStory3CustomExpert:
      typeof existing.lastStory3CustomExpert === 'string' &&
      existing.lastStory3CustomExpert.trim().length > 0
        ? existing.lastStory3CustomExpert.trim().slice(0, 48)
        : undefined,
    story3Notebook: mergeStory3Notebook(existing),
  };
}

function mergeStory3Notebook(
  existing: UserPreferences | undefined,
): Story3NotebookEntry[] | undefined {
  let notebook = normalizeStory3Notebook(existing?.story3Notebook);
  const legacy = notebookEntryFromLegacyDraft(
    (existing as UserPreferences & { story3Draft?: unknown })?.story3Draft,
  );
  if (
    legacy &&
    !notebook.some((e) => e.text.trim() === legacy.text.trim())
  ) {
    notebook = upsertNotebookEntry(notebook, legacy);
  }
  return notebook.length > 0 ? notebook : undefined;
}

function sanitizeStory3FeedbackMode(
  raw: unknown,
): Story3FeedbackModeId | undefined {
  if (
    raw === 'correction' ||
    raw === 'assessment' ||
    raw === 'question' ||
    raw === 'expert'
  ) {
    return raw;
  }
  return undefined;
}

function sanitizeStory3ExpertId(raw: unknown): Story3ExpertId | undefined {
  return typeof raw === 'string' && isStory3ExpertId(raw) ? raw : undefined;
}

function sanitizeStoredCustomPracticeKeys(
  raw: unknown,
): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string' || item.length === 0) continue;
    if (out.includes(item)) continue;
    out.push(item);
    if (out.length >= 5) break;
  }
  return out.length > 0 ? out : undefined;
}

function clampDictationUnitLength(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(7, Math.round(n)));
}

export const ALL_BADGES: ReadonlyArray<Omit<Badge, 'earnedAt'>> = [
  {
    id: 'first-lesson',
    name: 'Første lektion',
    description: 'Gennemfør din første lektion.',
  },
  {
    id: 'accuracy-95',
    name: '95% nøjagtighed',
    description: 'Afslut en lektion med mindst 95% nøjagtighed.',
  },
  {
    id: 'accuracy-98',
    name: '98% nøjagtighed',
    description: 'Afslut en lektion med mindst 98% nøjagtighed.',
  },
  {
    id: 'streak-3',
    name: '3 dage i træk',
    description: 'Øv i tre dage i træk.',
  },
  {
    id: 'grundniveau-complete',
    name: 'Grundniveau gennemført',
    description: 'Gennemfør alle trin i grundniveau med mindst 95% nøjagtighed.',
  },
  {
    id: 'hele-alfabetet',
    name: 'Hele alfabetet',
    description: 'Gennemfør Niveau 4 — alle bogstaver på tastaturet.',
  },
  {
    id: 'klar-til-saetninger',
    name: 'Klar til sætninger',
    description: 'Gennemfør Niveau 5 — Shift, store bogstaver og tegn.',
  },
  {
    id: 'fuld-blindskrift',
    name: 'Fuld blindskrift',
    description: 'Gennemfør Niveau 6 — flydighed og lange tekster.',
  },
  {
    id: 'diktatmester',
    name: 'Diktatmester',
    description:
      'Fuldfør 3 diktat-runder med mindst 95% nøjagtighed (samlet).',
  },
  {
    id: 'rytme-ven',
    name: 'Rytme-ven',
    description:
      'Fuldfør en metronom-runde med mindst 90% nøjagtighed på rytmen.',
  },
  {
    id: 'varm-op-5',
    name: 'Varm-op vane',
    description: '5 dages stribe med daglig varm-op.',
  },
  {
    id: 'trin-proof-first',
    name: 'Første trin-bevis',
    description: 'Bestå dit første trin-bevis.',
  },
  {
    id: 'baseline-set',
    name: 'Udgangspunkt sat',
    description: 'Gennemfør både afskrift- og hverdagstest.',
  },
];

export function emptyState(): AppState {
  return { version: 3.5, theme: 'light', users: [], activeUserId: null };
}

function isAppStateVersion(v: unknown): v is AppStateVersion {
  return (
    v === 3 ||
    v === 3.1 ||
    v === 3.2 ||
    v === 3.3 ||
    v === 3.4 ||
    v === 3.5
  );
}

export function loadAppState(): AppState {
  if (typeof localStorage === 'undefined') return emptyState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AppState> | null;
      if (
        parsed &&
        typeof parsed === 'object' &&
        isAppStateVersion(parsed.version) &&
        Array.isArray(parsed.users)
      ) {
        const loadedVersion = isAppStateVersion(parsed.version)
          ? parsed.version
          : 3;
        return {
          version: 3.5,
          theme: parsed.theme === 'dark' ? 'dark' : 'light',
          users: (parsed.users as UserProfile[]).map((user) =>
            normalizeUser(user, loadedVersion),
          ),
          activeUserId: parsed.activeUserId ?? null,
        };
      }
    }
  } catch {
    /* ignore */
  }
  return emptyState();
}

export function saveAppState(state: AppState): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function migrateBadges(badges: Badge[]): Badge[] {
  return badges.map((b) => {
    if ((b.id as string) === 'home-row-complete') {
      const def = ALL_BADGES.find((x) => x.id === 'grundniveau-complete');
      return def ? { ...def, earnedAt: b.earnedAt } : b;
    }
    return b;
  });
}

function backfillTrinRecords(user: UserProfile): Record<string, TrinRecord> {
  const out: Record<string, TrinRecord> = { ...(user.trinRecords ?? {}) };
  for (const r of user.recentResults) {
    if (!r.trinId) continue;
    const tid = migrateTrinId(r.trinId);
    const prev = out[tid];
    out[tid] = mergeLessonIntoTrinRecord(prev, { ...r, trinId: tid });
  }
  return out;
}

function backfillDailySummaries(
  user: UserProfile,
): Record<string, DailySummary> {
  const existing = user.dailySummaries ?? {};
  if (Object.keys(existing).length > 0) {
    return pruneDailySummaries(existing);
  }
  if (user.recentResults.length === 0) return {};
  return pruneDailySummaries(
    backfillDailySummariesFromRecentResults(user.recentResults),
  );
}

function applyDailySummary(
  user: UserProfile,
  result: LessonResult,
  xpGained: number,
): Record<string, DailySummary> {
  const date = localDateFromIso(result.startedAt);
  const merged = mergeLessonIntoDailySummary(
    user.dailySummaries?.[date],
    result,
    date,
    xpGained,
  );
  return pruneDailySummaries({
    ...(user.dailySummaries ?? {}),
    [date]: merged,
  });
}

function applyTrinIdMigration(user: UserProfile): UserProfile {
  return {
    ...user,
    currentTrinId: migrateTrinId(user.currentTrinId),
    completedTrinIds: migrateTrinIdList(user.completedTrinIds),
    recentResults: user.recentResults.map((r) => ({
      ...r,
      trinId: r.trinId ? migrateTrinId(r.trinId) : FIRST_TRIN_ID,
    })),
    trinRecords: Object.fromEntries(
      Object.entries(user.trinRecords ?? {}).map(([k, v]) => [
        migrateTrinId(k),
        v,
      ]),
    ),
  };
}

export function normalizeUser(
  u: UserProfile,
  fromVersion: AppStateVersion = 3.5,
): UserProfile {
  let user: UserProfile = {
    ...u,
    keyboardLayout: u.keyboardLayout ?? 'mac-dk',
    trinRecords: u.trinRecords ?? {},
    checkpointRecords: u.checkpointRecords ?? {},
    recentResults: (u.recentResults ?? []).map((r) =>
      r.trinId ? r : { ...r, trinId: FIRST_TRIN_ID, tierId: 'grundniveau' as const },
    ),
    badges: migrateBadges(u.badges ?? []),
    preferences: mergePreferences(u.preferences),
  };
  user = migrateUserToV31(user);
  const preV32Completed = [...user.completedTrinIds];
  const preV32Current = user.currentTrinId;
  user = migrateUserToV32(user, preV32Completed);
  user = migrateUserToV33(user);
  user = migrateUserToV34(user);
  if (fromVersion !== 3.5) {
    user = migrateUserToV35(user);
  }
  user = applyTrinIdMigration(user);
  if (/^niveau-1-trin-\d+$/.test(preV32Current) || preV32Current === 'grundniveau-trin-7') {
    user = { ...user, currentTrinId: migrateTrinId(preV32Current) };
  }
  user = {
    ...user,
    trinRecords: backfillTrinRecords(user),
    dailySummaries: backfillDailySummaries(user),
    trinProofRecords: user.trinProofRecords ?? {},
    miniCheckpointRecords: user.miniCheckpointRecords ?? {},
    passedMiniCheckpointIds: backfillPassedMiniCheckpoints(user),
  };
  const trin = getTrin(user.currentTrinId);
  if (!trin) {
    user = { ...user, currentTrinId: FIRST_TRIN_ID };
  }

  user = migrateBaselineTranscription(user);

  user = {
    ...user,
    baselineTranscription: normalizeBaselineSnapshot(
      user.baselineTranscription,
      'transcription',
    ),
    baselineTranscriptionSchemaVersion:
      typeof user.baselineTranscriptionSchemaVersion === 'number'
        ? user.baselineTranscriptionSchemaVersion
        : undefined,
    baselineComposition: normalizeBaselineSnapshot(
      user.baselineComposition,
      'composition',
    ),
    goals: user.onboardingCompletedAt
      ? normalizeUserGoals(user.goals)
      : user.goals,
    onboardingCompletedAt:
      typeof user.onboardingCompletedAt === 'string'
        ? user.onboardingCompletedAt
        : undefined,
    wpmCheckIns: normalizeWpmCheckIns(user.wpmCheckIns),
  };

  return backfillTypingProfile(syncBaselineSetBadge(user));
}

function normalizeWpmCheckIn(raw: WpmCheckIn): WpmCheckIn | undefined {
  if (!Number.isFinite(raw.wpm) || !Number.isFinite(raw.accuracy)) {
    return undefined;
  }
  const solidKeyCount = Math.max(
    0,
    Math.round(raw.solidKeyCount ?? 0),
  );
  return {
    wpm: Math.max(0, raw.wpm),
    accuracy: Math.max(0, Math.min(1, raw.accuracy)),
    correctChars: Math.max(0, Math.round(raw.correctChars ?? 0)),
    totalKeystrokes: Math.max(0, Math.round(raw.totalKeystrokes ?? 0)),
    backspaces: Math.max(0, Math.round(raw.backspaces ?? 0)),
    durationMs: Math.max(0, Math.round(raw.durationMs ?? 0)),
    takenAt:
      typeof raw.takenAt === 'string' && raw.takenAt
        ? raw.takenAt
        : new Date().toISOString(),
    solidKeyFingerprint:
      typeof raw.solidKeyFingerprint === 'string'
        ? raw.solidKeyFingerprint
        : '',
    solidKeyCount,
    textSeed: Math.round(raw.textSeed ?? 0),
  };
}

function normalizeWpmCheckIns(
  raw: WpmCheckIn[] | undefined,
): WpmCheckIn[] | undefined {
  if (!raw?.length) return undefined;
  const out = raw
    .map(normalizeWpmCheckIn)
    .filter((c): c is WpmCheckIn => c !== undefined);
  return out.length > 0 ? out : undefined;
}

function syncBaselineSetBadge(user: UserProfile): UserProfile {
  const qualifies =
    !!user.baselineTranscription && !!user.baselineComposition;
  const has = user.badges.some((b) => b.id === 'baseline-set');
  if (qualifies && !has) {
    const def = ALL_BADGES.find((b) => b.id === 'baseline-set');
    if (!def) return user;
    return {
      ...user,
      badges: [
        ...user.badges,
        { ...def, earnedAt: new Date().toISOString() },
      ],
    };
  }
  if (!qualifies && has) {
    return {
      ...user,
      badges: user.badges.filter((b) => b.id !== 'baseline-set'),
    };
  }
  return user;
}

/** @deprecated use syncBaselineSetBadge */
function maybeGrantBaselineBadge(user: UserProfile): UserProfile {
  return syncBaselineSetBadge(user);
}

function resolveUserRole(name: string, existing?: UserRole): UserRole {
  if (existing === 'tester' && import.meta.env.DEV) return 'tester';
  if (import.meta.env.DEV && /^admin$/i.test(name.trim())) return 'tester';
  return existing ?? 'learner';
}

export function createUser(name: string): UserProfile {
  const now = new Date().toISOString();
  const trimmed = name.trim() || 'Bruger';
  const role = resolveUserRole(trimmed);
  return {
    id: cryptoRandomId(),
    name: trimmed,
    createdAt: now,
    lastActiveAt: now,
    role,
    keyboardLayout: 'mac-dk',
    currentTrinId: FIRST_TRIN_ID,
    completedTrinIds: [],
    completedTierIds: [],
    xp: 0,
    streak: { count: 0, lastLessonDate: null },
    errorsByKey: {},
    responseTimeAvgs: {},
    wordStats: {},
    errorsByBigram: {},
    transitionTimeAvgs: {},
    recentResults: [],
    dailySummaries: {},
    trinRecords: {},
    checkpointRecords: {},
    trinProofRecords: {},
    miniCheckpointRecords: {},
    passedMiniCheckpointIds: [],
    badges: [],
    preferences: defaultPreferences(),
  };
}

function backfillPassedMiniCheckpoints(user: UserProfile): string[] {
  const passed = new Set(user.passedMiniCheckpointIds ?? []);
  for (const mc of MINI_CHECKPOINTS) {
    if (passed.has(mc.id)) continue;
    const gateIdx = trinIndex(mc.afterTrinId);
    if (gateIdx < 0) continue;
    const pastGate =
      user.completedTrinIds.includes(mc.afterTrinId) ||
      trinIndex(user.currentTrinId) > gateIdx;
    if (pastGate) passed.add(mc.id);
  }
  return [...passed];
}

export function setUserPreferences(
  user: UserProfile,
  patch: UserPreferencesPatch,
): UserProfile {
  const { metronomeBpm, ...rest } = patch;
  const merged: UserPreferences = {
    ...(user.preferences ?? defaultPreferences()),
    ...rest,
  };
  if (metronomeBpm === null) {
    delete merged.metronomeBpm;
  } else if (metronomeBpm !== undefined) {
    merged.metronomeBpm = metronomeBpm;
  }
  if (rest.story3Notebook !== undefined) {
    merged.story3Notebook =
      normalizeStory3Notebook(rest.story3Notebook).length > 0
        ? normalizeStory3Notebook(rest.story3Notebook)
        : undefined;
  }
  const next = mergePreferences(merged);
  return { ...user, preferences: next };
}

export function setUserBaseline(
  user: UserProfile,
  snapshot: BaselineSnapshot,
): UserProfile {
  const merged = maybeGrantBaselineBadge(mergeBaseline(user, snapshot));
  return recordActivity(merged, activityPayloadFromBaseline(snapshot));
}

export function addWpmCheckIn(
  user: UserProfile,
  checkIn: WpmCheckIn,
): UserProfile {
  const normalized = normalizeWpmCheckIn(checkIn);
  if (!normalized) return user;
  const wpmCheckIns = [normalized, ...(user.wpmCheckIns ?? [])];
  return recordActivity(
    {
      ...user,
      wpmCheckIns,
    },
    {
      kind: 'wpm-check-in',
      at: normalized.takenAt,
      durationMs: normalized.durationMs,
      totalKeystrokes: normalized.totalKeystrokes,
      correctChars: normalized.correctChars,
      wpm: normalized.wpm,
      accuracy: normalized.accuracy,
    },
  );
}

export function setUserGoals(
  user: UserProfile,
  goals: UserGoals,
): UserProfile {
  return { ...user, goals: normalizeUserGoals(goals) };
}

export function completeOnboarding(
  user: UserProfile,
  goals: UserGoals,
): UserProfile {
  const now = new Date().toISOString();
  return maybeGrantBaselineBadge({
    ...user,
    goals: normalizeUserGoals(goals),
    onboardingCompletedAt: now,
    lastActiveAt: now,
  });
}

export function upsertUser(state: AppState, user: UserProfile): AppState {
  const idx = state.users.findIndex((u) => u.id === user.id);
  const users =
    idx === -1
      ? [...state.users, user]
      : state.users.map((u, i) => (i === idx ? user : u));
  return { ...state, users };
}

export function setActiveUser(state: AppState, id: string): AppState {
  const now = new Date().toISOString();
  const users = state.users.map((u) =>
    u.id === id ? { ...u, lastActiveAt: now } : u,
  );
  return { ...state, users, activeUserId: id };
}

export function removeUser(state: AppState, userId: string): AppState {
  const users = state.users.filter((u) => u.id !== userId);
  const activeUserId =
    state.activeUserId === userId ? (users[0]?.id ?? null) : state.activeUserId;
  return { ...state, users, activeUserId };
}

export function getActiveUser(state: AppState): UserProfile | null {
  if (!state.activeUserId) return null;
  return state.users.find((u) => u.id === state.activeUserId) ?? null;
}

export function setTheme(
  state: AppState,
  theme: 'light' | 'dark',
): AppState {
  return { ...state, theme };
}

function localDateKey(d: Date = new Date()): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function daysBetween(a: string, b: string): number {
  const da = new Date(a + 'T00:00:00');
  const db = new Date(b + 'T00:00:00');
  return Math.round((db.getTime() - da.getTime()) / 86_400_000);
}

function recomputeCompletedTiers(
  completedTrinIds: string[],
  existing: CurriculumTierId[],
): CurriculumTierId[] {
  const out = new Set(existing);
  const tiers: CurriculumTierId[] = [
    'grundniveau',
    'niveau-1',
    'niveau-2',
    'niveau-3',
    'niveau-4',
    'niveau-5',
    'niveau-6',
  ];
  for (const tierId of tiers) {
    const ids = allTrinIdsInTier(tierId);
    if (ids.length > 0 && ids.every((id) => completedTrinIds.includes(id))) {
      out.add(tierId);
    }
  }
  return [...out];
}

export interface AddLessonResultOptions {
  /**
   * If true, the lesson does not advance progression: trin records, completed
   * trin/tier ids and currentTrinId are left untouched. Streak, errors,
   * response times, XP and badges still update. Used for daily warm-up rounds
   * that aren't part of the curriculum.
   */
  skipProgression?: boolean;
}

export function addLessonResult(
  user: UserProfile,
  result: LessonResult,
  opts: AddLessonResultOptions = {},
): UserProfile {
  const today = localDateKey();

  let streakCount = user.streak.count;
  if (user.streak.lastLessonDate === null) {
    streakCount = 1;
  } else {
    const delta = daysBetween(user.streak.lastLessonDate, today);
    if (delta === 1) streakCount += 1;
    else if (delta > 1) streakCount = 1;
  }
  const streak = { count: streakCount, lastLessonDate: today };

  const profile = mergeTypingProfileFromLesson(user, result);

  const xpGain = xpForLesson(result);
  const xp = user.xp + xpGain;
  const recentResults = [result, ...user.recentResults].slice(
    0,
    RECENT_RESULTS_CAP,
  );
  const dailySummaries = applyDailySummary(user, result, xpGain);

  if (opts.skipProgression) {
    const badges = awardBadges(user.badges, {
      streakCount,
      result,
      recentResults,
      completedTrinIds: user.completedTrinIds,
      completedTierIds: user.completedTierIds,
    });
    return recordActivity(
      {
        ...user,
        streak,
        ...profile,
        xp,
        recentResults,
        dailySummaries,
        badges,
      },
      activityPayloadFromLesson(result, xpGain),
      { skipDaily: true },
    );
  }

  const cleared = result.accuracy >= 0.95 && result.passedAllRounds;
  const deferProgression =
    cleared &&
    PROGRESSION.trinProof &&
    PROGRESSION.requireTrinProof &&
    !user.trinProofRecords?.[result.trinId]?.lastPassedAt;

  let completedTrinIds = user.completedTrinIds;
  let currentTrinId = user.currentTrinId;
  if (cleared && !deferProgression) {
    if (!completedTrinIds.includes(result.trinId)) {
      completedTrinIds = [...completedTrinIds, result.trinId];
    }
    const next = getNextTrin(result.trinId);
    if (next) currentTrinId = next.id;
  }

  const trinRecords = {
    ...user.trinRecords,
    [result.trinId]: mergeLessonIntoTrinRecord(
      user.trinRecords[result.trinId],
      result,
    ),
  };

  const completedTierIds = recomputeCompletedTiers(
    completedTrinIds,
    user.completedTierIds,
  );

  const badges = awardBadges(user.badges, {
    streakCount,
    result,
    recentResults,
    completedTrinIds,
    completedTierIds,
  });

  return recordActivity(
    {
      ...user,
      streak,
      ...profile,
      xp,
      recentResults,
      dailySummaries,
      trinRecords,
      badges,
      completedTrinIds,
      completedTierIds,
      currentTrinId,
    },
    activityPayloadFromLesson(result, xpGain),
    { skipDaily: true },
  );
}

/** Trin-bevis afsluttet — progression først når bevis bestås. */
export function addTrinProofResult(
  user: UserProfile,
  proof: TrinProofResult,
): UserProfile {
  const prev = user.trinProofRecords?.[proof.trinId];
  const trinProofRecords = {
    ...(user.trinProofRecords ?? {}),
    [proof.trinId]: {
      bestAccuracy: Math.max(prev?.bestAccuracy ?? 0, proof.accuracy),
      attempts: (prev?.attempts ?? 0) + 1,
      lastPassedAt: proof.passed
        ? proof.startedAt
        : (prev?.lastPassedAt ?? null),
    },
  };

  let completedTrinIds = user.completedTrinIds;
  let currentTrinId = user.currentTrinId;
  if (proof.passed) {
    if (!completedTrinIds.includes(proof.trinId)) {
      completedTrinIds = [...completedTrinIds, proof.trinId];
    }
    const next = getNextTrin(proof.trinId);
    if (next) currentTrinId = next.id;
  }

  const completedTierIds = recomputeCompletedTiers(
    completedTrinIds,
    user.completedTierIds,
  );

  const badges = awardBadges(user.badges, {
    streakCount: user.streak.count,
    result: {
      trinId: proof.trinId,
      tierId: getTrin(proof.trinId)?.tierId ?? 'grundniveau',
      startedAt: proof.startedAt,
      durationMs: proof.durationMs,
      rounds: [],
      wpm: proof.wpm,
      accuracy: proof.accuracy,
      correctChars: 0,
      totalKeystrokes: 0,
      mistakes: 0,
      backspaces: 0,
      errorsByKey: {},
      responseTimes: {},
      passedAllRounds: proof.passed,
    },
    recentResults: user.recentResults,
    completedTrinIds,
    completedTierIds,
    trinProofPassed: proof.passed,
  });

  const xpGain = proof.passed ? 25 : 5;
  const xp = user.xp + xpGain;

  return recordActivity(
    {
      ...user,
      xp,
      trinProofRecords,
      completedTrinIds,
      completedTierIds,
      currentTrinId,
      badges,
    },
    {
      kind: 'trin-proof',
      at: proof.startedAt,
      durationMs: proof.durationMs,
      totalKeystrokes: proof.totalKeystrokes ?? 0,
      correctChars: proof.correctChars ?? 0,
      wpm: proof.wpm,
      accuracy: proof.accuracy,
      trinId: proof.trinId,
      xpGained: xpGain,
    },
  );
}

/** Mini-checkpoint afsluttet — gem rekord og evt. bestået-status. */
export function addMiniCheckpointResult(
  user: UserProfile,
  result: MiniCheckpointResult,
): UserProfile {
  const prev = user.miniCheckpointRecords?.[result.checkpointId];
  const record: CheckpointRecord = {
    bestWords: Math.max(prev?.bestWords ?? 0, result.wordsCompleted),
    bestAccuracy: Math.max(prev?.bestAccuracy ?? 0, result.accuracy),
    attempts: (prev?.attempts ?? 0) + 1,
    lastPlayedAt: result.startedAt,
  };
  const miniCheckpointRecords = {
    ...(user.miniCheckpointRecords ?? {}),
    [result.checkpointId]: record,
  };

  let passedMiniCheckpointIds = user.passedMiniCheckpointIds ?? [];
  if (
    result.passed &&
    !passedMiniCheckpointIds.includes(result.checkpointId)
  ) {
    passedMiniCheckpointIds = [
      ...passedMiniCheckpointIds,
      result.checkpointId,
    ];
  }

  const xpGain = result.passed ? 40 : 12;
  const xp = user.xp + xpGain;
  const est = estimateKeystrokesFromWords(result.wordsCompleted);

  return recordActivity(
    {
      ...user,
      xp,
      miniCheckpointRecords,
      passedMiniCheckpointIds,
    },
    {
      kind: 'mini-checkpoint',
      at: result.startedAt,
      durationMs: result.durationMs,
      totalKeystrokes: est.totalKeystrokes,
      correctChars: est.correctChars,
      accuracy: result.accuracy,
      checkpointId: result.checkpointId,
      xpGained: xpGain,
    },
  );
}

/** Checkpoint (ord-sprint) afsluttet — gem rekord; rør ikke currentTrinId. */
export function addCheckpointResult(
  user: UserProfile,
  checkpointId: string,
  wordsCompleted: number,
  startedAt: string,
): { user: UserProfile; isNewRecord: boolean } {
  const checkpoint = getCheckpoint(checkpointId);
  if (!checkpoint) return { user, isNewRecord: false };

  const today = localDateKey();
  let streakCount = user.streak.count;
  if (user.streak.lastLessonDate === null) {
    streakCount = 1;
  } else {
    const delta = daysBetween(user.streak.lastLessonDate, today);
    if (delta === 1) streakCount += 1;
    else if (delta > 1) streakCount = 1;
  }
  const streak = { count: streakCount, lastLessonDate: today };

  const prev = user.checkpointRecords[checkpointId];
  const prevBest = prev?.bestWords ?? 0;
  const isNewRecord = wordsCompleted > prevBest;

  const checkpointRecords = {
    ...user.checkpointRecords,
    [checkpointId]: mergeCheckpointRecord(prev, wordsCompleted, startedAt),
  };

  const xp = user.xp + Math.min(80, 30 + wordsCompleted * 2);

  const badges = awardBadges(user.badges, {
    streakCount,
    result: {
      trinId: checkpointId,
      tierId: checkpoint.afterTierId,
      startedAt,
      durationMs: checkpoint.sprintDurationMs ?? 60_000,
      rounds: [],
      wpm: 0,
      accuracy: 1,
      correctChars: 0,
      totalKeystrokes: 0,
      mistakes: 0,
      backspaces: 0,
      errorsByKey: {},
      responseTimes: {},
      passedAllRounds: true,
    },
    recentResults: user.recentResults,
    completedTrinIds: user.completedTrinIds,
    completedTierIds: user.completedTierIds,
  });

  const est = estimateKeystrokesFromWords(wordsCompleted);
  const xpGain = Math.min(80, 30 + wordsCompleted * 2);

  return {
    user: recordActivity(
      {
        ...user,
        streak,
        xp,
        checkpointRecords,
        badges,
      },
      {
        kind: 'checkpoint-sprint',
        at: startedAt,
        durationMs: checkpoint.sprintDurationMs ?? 60_000,
        totalKeystrokes: est.totalKeystrokes,
        correctChars: est.correctChars,
        checkpointId,
        xpGained: xpGain,
      },
    ),
    isNewRecord,
  };
}

/**
 * Dev-only: mark all trins before `trinId` as completed and set it as current.
 * Unlocks tiers/checkpoints/warmup eligibility as if the user had played through.
 */
export function devJumpToTrin(
  user: UserProfile,
  trinId: string,
): UserProfile | null {
  if (!import.meta.env.DEV) return null;
  const trin = getTrin(trinId);
  if (!trin) return null;
  const targetIdx = trinIndex(trinId);
  if (targetIdx < 0) return null;

  const completedTrinIds = new Set(user.completedTrinIds);
  for (let i = 0; i < targetIdx; i++) {
    completedTrinIds.add(ALL_TRINS[i].id);
  }

  const completedTierIds = new Set(user.completedTierIds);
  for (const tier of CURRICULUM) {
    if (tier.trins.length === 0) continue;
    if (tier.trins.every((t) => completedTrinIds.has(t.id))) {
      completedTierIds.add(tier.id);
    }
  }

  return {
    ...user,
    currentTrinId: trinId,
    completedTrinIds: [...completedTrinIds],
    completedTierIds: [...completedTierIds],
  };
}

/** Sæt aktivt trin til første trin i næste tier (efter checkpoint). */
export function advanceAfterCheckpoint(
  user: UserProfile,
  checkpointId: string,
): UserProfile {
  const checkpoint = getCheckpoint(checkpointId);
  if (!checkpoint) return user;
  const nextId = firstTrinIdInTier(checkpoint.beforeTierId);
  if (!nextId) return user;
  return { ...user, currentTrinId: nextId };
}

interface BadgeContext {
  streakCount: number;
  result: LessonResult;
  recentResults: LessonResult[];
  completedTrinIds: string[];
  completedTierIds: CurriculumTierId[];
  trinProofPassed?: boolean;
}

function awardBadges(existing: Badge[], ctx: BadgeContext): Badge[] {
  const now = new Date().toISOString();
  const have = new Set(existing.map((b) => b.id));
  const out = [...existing];

  const grant = (id: BadgeId) => {
    if (have.has(id)) return;
    const def = ALL_BADGES.find((b) => b.id === id);
    if (!def) return;
    out.push({ ...def, earnedAt: now });
    have.add(id);
  };

  grant('first-lesson');
  if (ctx.result.accuracy >= 0.95) grant('accuracy-95');
  if (ctx.result.accuracy >= 0.98) grant('accuracy-98');
  if (ctx.streakCount >= 3) grant('streak-3');

  if (ctx.completedTierIds.includes('grundniveau')) {
    grant('grundniveau-complete');
  }
  if (ctx.completedTierIds.includes('niveau-4')) {
    grant('hele-alfabetet');
  }
  if (ctx.completedTierIds.includes('niveau-5')) {
    grant('klar-til-saetninger');
  }
  if (ctx.completedTierIds.includes('niveau-6')) {
    grant('fuld-blindskrift');
  }

  // Diktatmester: at least 3 dictation rounds across history with >=95% acc.
  let highAccDictation = 0;
  for (const r of ctx.recentResults) {
    for (const round of r.rounds) {
      if (
        (round.presentationKind ?? 'standard') === 'dictation' &&
        round.accuracy >= 0.95 &&
        round.completed
      ) {
        highAccDictation += 1;
      }
    }
  }
  if (highAccDictation >= 3) grant('diktatmester');

  // Rytme-ven: any metronome round with >=90% accuracy in this lesson.
  for (const round of ctx.result.rounds) {
    if (
      (round.presentationKind ?? 'standard') === 'metronome' &&
      round.accuracy >= 0.9 &&
      round.completed
    ) {
      grant('rytme-ven');
      break;
    }
  }

  // Varm-op vane: 5 distinct days with at least one warm-up lesson.
  const warmupDays = new Set<string>();
  for (const r of ctx.recentResults) {
    if (r.trinId === WARMUP_TRIN_ID) {
      warmupDays.add(r.startedAt.slice(0, 10));
    }
  }
  if (warmupDays.size >= 5) grant('varm-op-5');

  if (ctx.trinProofPassed) grant('trin-proof-first');

  return out;
}

function cryptoRandomId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return 'u_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
